import React, { useState, useEffect } from 'react'

import {
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Box,
    Grid,
    Paper,
} from '@material-ui/core'
import {
    Add as Plus,
    Description as DocumentIcon,
    Search,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { axiosInstance } from '../../../helpers'
import { LeaseAuditStatus } from '../../../models/DocumentAudit/types'
import { EzosLoader } from '../../../components'
import { LeaseAuditCard } from './LeaseAuditCard'
import { CreateLeaseAuditModal } from './CreateLeaseAuditModal'
import { useUser } from '../../../hooks/useUser'
import { isAccountManager } from '../../../helpers/stringHelpers'
import { useProspectList } from '../../../hooks/Outreach/useProspectList'
import { LeaseAuditDetail } from '../types'

const useStyles = makeStyles((theme: Theme) => ({
    heroSection: {
        background: `linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)`,
        borderRadius: theme.spacing(4),
        padding: theme.spacing(4),
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    heroTitle: {
        fontSize: '2.5rem',
        fontWeight: 'bold',
        marginBottom: theme.spacing(1),
    },
    heroSubtitle: {
        fontSize: '1.125rem',
        opacity: 0.8,
    },
    createButton: {
        backgroundColor: 'white',
        color: '#3b82f6',
        fontSize: '1rem',
        fontWeight: 600,
        padding: '12px 24px',
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            transform: 'scale(1.05)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
        boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    searchContainer: {
        marginTop: theme.spacing(3),
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    searchField: {
        flex: 1,
        minWidth: '200px',
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            borderRadius: '8px',
            '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.6)',
        },
        '& .MuiInputBase-input::placeholder': {
            color: 'rgba(255, 255, 255, 0.6)',
        },
    },
    filterButton: {
        color: 'rgba(255, 255, 255, 0.8)',
        borderRadius: '6px',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
        },
        '&.active': {
            backgroundColor: 'white',
            color: '#3b82f6',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
    },
    statsCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
    },
    emptyStateContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(8),
    },
    emptyStateIcon: {
        padding: theme.spacing(2),
        borderRadius: '50%',
        backgroundColor: '#f1f5f9',
        marginBottom: theme.spacing(2),
    },
}))

type StatusFilter = 'all' | LeaseAuditStatus

interface StatusFilterOption {
    value: StatusFilter
    label: string
}

const STATUS_FILTERS: StatusFilterOption[] = [
    { value: 'all', label: 'All' },
    { value: 'REQUESTED', label: 'Requested' },
    { value: 'SETUP', label: 'Setup' },
    { value: 'RUNNING_PIPELINE', label: 'Running' },
    { value: 'COMPLETED', label: 'Completed' },
]

export const LeaseAuditHome: React.FC = () => {
    const classes = useStyles()
    const { workspaceUser } = useUser()
    const superUser = isAccountManager(workspaceUser)
    const { prospectList } = useProspectList()

    const [leaseAudits, setLeaseAudits] = useState<LeaseAuditDetail[]>([])
    const [loading, setLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [selectedAuditId, setSelectedAuditId] = useState<number | null>(null)

    // Create modal state
    const [modalOpen, setModalOpen] = useState(false)

    // Fetch lease audits
    useEffect(() => {
        const fetchLeaseAudits = async () => {
            try {
                setLoading(true)
                const response = await axiosInstance.get<any>('/lease-audit/')
                // Handle both paginated and non-paginated responses
                const audits = response.data.results || response.data
                setLeaseAudits(audits)
            } catch (error) {
                console.error('Error fetching lease audits:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchLeaseAudits()
    }, [])

    // Sort audits by created date (newest first)
    const sortedAudits = [...leaseAudits].sort((a, b) => {
        return (
            new Date(b.created_date).getTime() -
            new Date(a.created_date).getTime()
        )
    })

    // Filter audits based on search and status
    const filteredAudits = sortedAudits.filter((audit) => {
        const matchesSearch = audit.name
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        const matchesStatus =
            statusFilter === 'all' || audit.status === statusFilter
        return matchesSearch && matchesStatus
    })

    // Calculate stats
    const stats = {
        totalDocuments: leaseAudits.reduce(
            (sum, audit) => sum + audit.document_count,
            0,
        ),
        totalPages: leaseAudits.reduce(
            (sum, audit) => sum + audit.page_count,
            0,
        ),
        avgPagesPerDoc: (() => {
            const totalDocs = leaseAudits.reduce(
                (sum, audit) => sum + audit.document_count,
                0,
            )
            const totalPages = leaseAudits.reduce(
                (sum, audit) => sum + audit.page_count,
                0,
            )
            return totalDocs > 0 ? (totalPages / totalDocs).toFixed(2) : '0'
        })(),
        tokensRemaining: prospectList?.[0]?.tokens_remaining,
    }

    const handleMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
        auditId: number,
    ) => {
        setAnchorEl(event.currentTarget)
        setSelectedAuditId(auditId)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        setSelectedAuditId(null)
    }

    const handleArchiveAudit = async () => {
        if (selectedAuditId) {
            try {
                await axiosInstance.delete(`/lease-audit/${selectedAuditId}/`)
                // Remove the archived audit from the list
                setLeaseAudits((prev) =>
                    prev.filter((audit) => audit.id !== selectedAuditId),
                )
            } catch (error) {
                console.error('Error archiving lease audit:', error)
            }
        }
        handleMenuClose()
    }

    const handleOpenModal = () => {
        setModalOpen(true)
    }

    const handleCloseModal = () => {
        setModalOpen(false)
    }

    const handleModalSuccess = async () => {
        // Refresh the audit list after successful creation
        try {
            const response = await axiosInstance.get<any>('/lease-audit/')
            // Handle both paginated and non-paginated responses
            const audits = response.data.results || response.data
            setLeaseAudits(audits)
        } catch (error) {
            console.error('Error refreshing lease audits:', error)
        }
    }

    return (
        <Box>
            {/* Hero Section */}
            <Paper className={classes.heroSection} elevation={8}>
                <Box
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    alignItems={{ md: 'center' }}
                    justifyContent={{ md: 'space-between' }}
                    style={{ gap: 24 }}
                >
                    <Box>
                        <Typography variant="h3" className={classes.heroTitle}>
                            Lease Audits
                        </Typography>
                        <Typography
                            variant="h6"
                            className={classes.heroSubtitle}
                        >
                            AI-powered document auditing and analysis
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        size="large"
                        className={classes.createButton}
                        startIcon={<Plus />}
                        onClick={handleOpenModal}
                    >
                        Create New Audit
                    </Button>
                </Box>
            </Paper>

            {/* Audit Cards Grid */}
            {!loading && (
                <Grid container spacing={3} style={{ marginTop: 16 }}>
                    {filteredAudits.map((audit) => (
                        <Grid item xs={12} lg={6} key={audit.id}>
                            <LeaseAuditCard
                                audit={audit}
                                onMenuOpen={handleMenuOpen}
                                superUser={superUser}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Loading State */}
            {loading && (
                <Card className={classes.statsCard} style={{ marginTop: 16 }}>
                    <CardContent>
                        <EzosLoader />
                    </CardContent>
                </Card>
            )}

            {/* Empty State */}
            {!loading && filteredAudits.length === 0 && (
                <Card className={classes.statsCard} style={{ marginTop: 16 }}>
                    <CardContent className={classes.emptyStateContainer}>
                        <Box className={classes.emptyStateIcon}>
                            <DocumentIcon color="action" />
                        </Box>
                        <Typography variant="h6" gutterBottom>
                            No lease audits found
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            align="center"
                            paragraph
                        >
                            {searchQuery
                                ? 'Try adjusting your search or filters'
                                : 'Create your first lease audit to get started'}
                        </Typography>
                        {!searchQuery && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Plus />}
                                onClick={handleOpenModal}
                            >
                                Create Your First Audit
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleArchiveAudit}>Archive Audit</MenuItem>
            </Menu>

            {/* Create Audit Modal */}
            <CreateLeaseAuditModal
                open={modalOpen}
                onClose={handleCloseModal}
                onSuccess={handleModalSuccess}
            />
        </Box>
    )
}
